import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { HttpUtilsService } from '../../../core/_base/crud';
import { AppState } from '../../../core/reducers';
import { currentAuthToken } from '../../../core/auth';
import { select, Store } from '@ngrx/store';
import { environment } from '../../../../environments/environment';
import { impersonationId } from '../../../core/auth/_selectors/auth.selectors';
import { BridgePayAuthService } from '../../../core/bridge-pay/services/bridgepay-auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authToken: any;
  private impersonatedId;
  constructor(
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
    private bridgePayAuth: BridgePayAuthService,
  ) {
    this.store
      .pipe(select(currentAuthToken))
      .subscribe((res) => (this.authToken = res ? res : this.authToken));
    this.store.select(impersonationId).subscribe((val) => (this.impersonatedId = val));
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.url.indexOf('/api/v2/auth/session') !== -1) {
      return next.handle(request);
    }

    const req = request;

    // Skip auth for external APIs
    if (
      request.url.indexOf(
        'https://browser.ihtsdotools.org/snowstorm/snomed-ct/browser/MAIN/2020-07-31/descriptions',
      ) > -1 ||
      request.url.indexOf('172.17.2.4') > -1 ||
      request.url.indexOf('api.medplus.co') > -1 ||
      request.url.indexOf(environment.QRVEY_DOMAIN) > -1 ||
      request.url.indexOf('https://plus.codes/api') > -1 ||
      request.url.indexOf('X-Amz-Algorithm') > -1 ||
      request.url.indexOf(`${environment.QRVEY_DOMAIN}/loginToken`) > -1
    ) {
      return next.handle(req);
    }

    // Check if this is a BridgePay request
    const isBridgePayRequest = request.url.includes(environment.BRIDGEPAY_BASE);

    if (isBridgePayRequest) {
      // Token exchange endpoint doesn't need auth header (API key is in body)
      if (request.url.includes('/auth/token')) {
        return next.handle(request);
      }

      // Use BridgePay Bearer token for all other BridgePay endpoints
      return from(this.bridgePayAuth.getToken()).pipe(
        switchMap((bridgePayToken) => {
          let headers = request.headers;

          if (bridgePayToken) {
            headers = headers.set('Authorization', `Bearer ${bridgePayToken}`);
          }

          // Add Content-Type if not already set
          if (!headers.has('Content-Type') && !(request.body instanceof FormData)) {
            headers = headers.set('Content-Type', 'application/json');
          }

          const clonedRequest = request.clone({ headers });
          return next.handle(clonedRequest);
        }),
      );
    }

    // Use Knostos token for all other requests
    let httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken, this.impersonatedId);
    if (request.url.indexOf('/importCSV') > -1 || request.body instanceof FormData) {
      httpHeaders = this.httpUtils.getHTTPHeadersformCSV(this.authToken);
    }
    let headers = request.headers;
    (httpHeaders.keys() || []).forEach((key) => {
      headers = headers.set(key, httpHeaders.get(key));
    });
    request = request.clone({
      headers,
      withCredentials: true,
    });
    return next.handle(request);
  }
}
